/* ============================================================
   Ironstone Catalogue V3 - motion runtime
   Hero: a sticky stage scrubbed by scroll (veil exit -> product
   zoom-to-fill -> gallery sheet rides over). The CTA drives the
   same timeline by tweening the scroll position. Collection
   cycling = veil cover + real navigation, with every veil style
   owning a distinct cover/peel (curve plate scale, chevron line
   panels, chevron-arc ink bloom from the pivot, frame scale,
   blinds louvers flipping shut then open). All hero copy fades
   as one across a transition. Gallery: IO reveals, hover/scroll
   angle swaps, accent rail. Reduced motion or no GSAP => static
   composed hero, plain navigation.
   Idempotent; safe to re-run on shopify:section:load.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = window.matchMedia('(pointer: fine)').matches;
  var hasST = function () { return !!(window.gsap && window.ScrollTrigger); };

  /* The mobile veil's RESTING clip lives in CSS (--ct-mobile-arc-clip) because
     it is art direction, not behaviour: V8.4 rested it at 58%, V8.9 rests it
     at 100%, i.e. invisible. This used to be the literal 'inset(0 0 52% 0)'
     hardcoded at the one place that animates the shape back after a catalogue
     swap, so CSS and JS silently disagreed and an arrived-at hero rested at a
     different height from a freshly loaded one. Read the live value instead,
     and the two can only ever agree. */
  var mobileArcRest = function () {
    var v = '';
    try {
      v = getComputedStyle(document.documentElement).getPropertyValue('--ct-mobile-arc-clip').trim();
      if (!v) {
        var hero = document.querySelector('.ct-hero');
        if (hero) v = getComputedStyle(hero).getPropertyValue('--ct-mobile-arc-clip').trim();
      }
    } catch (e) {}
    return 'inset(0 0 ' + (v || '52%') + ' 0)';
  };

  document.documentElement.classList.remove('no-js');

  /* V8 gives the catalogue one scroll owner at every width. Horizon normally
     swaps from .page-wrapper on desktop to window scrolling below 990px. A
     viewport resize then leaves ScrollTrigger attached to the old element,
     collapsing the hero timeline while the page itself keeps scrolling.
     catalogue.css releases Horizon's desktop scroll lock, so the catalogue
     always uses the window and remains connected through every resize. */
  function detectScroller() {
    return null;
  }

  /* Near-uniform velocity: gentle at the ends, no cubic mid-rush. */
  function easeInOutSine(t) {
    return -(Math.cos(Math.PI * t) - 1) / 2;
  }
  function easeMostlyLinear(t) {
    return t - (Math.sin(t * Math.PI * 2) * 0.045);
  }
  /* Trapezoid velocity: quick ramp up, constant (linear) middle, quick ramp
     down. The velocity rises to a plateau over the first 20%, holds flat, then
     falls over the last 20%. Feels uniform and in control, never the
     slow-crawl-then-snap of an eased scrub. */
  function trapEase(t) {
    var r = 0.2;
    var v = 1 / (1 - r);
    if (t < r) return (v * t * t) / (2 * r);
    if (t > 1 - r) { var u = 1 - t; return 1 - (v * u * u) / (2 * r); }
    return v * (t - r / 2);
  }
  function normalizeVeilStyle(style) {
    if (style === 'chevron') return 'chevron-line';
    if (style === 'honeycomb') return 'curve';
    return style || 'curve';
  }
  function isChevronStyle(style) {
    style = normalizeVeilStyle(style);
    return typeof style === 'string' && style.indexOf('chevron') === 0;
  }
  function isArcStyle(style) {
    style = normalizeVeilStyle(style);
    return style === 'chevron-arc' || style === 'chevron-arc-fill';
  }

  /* Chevron-arc-fill cover/peel: the arc SVG path is morphed between its rest
     ARROW (whatever coverage produced, read live off the element) and a FULL
     rectangle, so the veil shape itself grows to seal the hero and shrinks back.
     Both paths share the command structure M L L C L L Z (16 numbers). */
  /* The FULL cover target for an arc: keep the shape's OWN y's, push every
     right-side x to 100 and hold the two left-edge points at 0, so the cover is
     a clean horizontal expansion to a full rectangle for ANY arc shape (the low
     classic/wide/modern arcs all seal correctly, whatever their Y coords). */
  function arcFull(rest) {
    var f = rest.slice();
    for (var i = 0; i < 16; i += 2) { f[i] = (i === 0 || i === 14) ? 0 : 100; }
    return f;
  }
  function arcNumsStr(d) {
    var m = d && d.match(/-?\d*\.?\d+/g);
    return m && m.length === 16 ? m.map(Number) : null;
  }
  function arcNums(pathEl) {
    return pathEl ? arcNumsStr(pathEl.getAttribute('d')) : null;
  }
  /* The rest ARROW path, taken from the static data attribute (never the live
     path, which may currently be morphed to FULL when a covering veil is cloned
     into the next page). This is what the cover grows from and the peel returns to. */
  function arcRestNums(arcShape) {
    return (arcShape && arcNumsStr(arcShape.getAttribute('data-ct-arc-rest'))) ||
      (arcShape && arcNums(arcShape.querySelector('path')));
  }
  function arcDStr(n) {
    return 'M ' + n[0] + ' ' + n[1] + ' L ' + n[2] + ' ' + n[3] + ' L ' + n[4] + ' ' + n[5] +
      ' C ' + n[6] + ' ' + n[7] + ' ' + n[8] + ' ' + n[9] + ' ' + n[10] + ' ' + n[11] +
      ' L ' + n[12] + ' ' + n[13] + ' L ' + n[14] + ' ' + n[15] + ' Z';
  }
  function arcMorph(pathEl, from, to, t) {
    var n = [];
    for (var i = 0; i < 16; i++) n.push(from[i] + (to[i] - from[i]) * t);
    var nextD = arcDStr(n);
    pathEl.setAttribute('d', nextD);
    /* V8's lightweight bevel uses the same live geometry as the face, so the
       highlight follows every cover and peel without adding a filtered layer. */
    var edgePaths = pathEl.parentNode && pathEl.parentNode.querySelectorAll('[data-ct-arc-edge]');
    if (edgePaths) {
      for (var e = 0; e < edgePaths.length; e++) edgePaths[e].setAttribute('d', nextD);
    }
  }

  /* ================= INDEX overlay ================= */
  /* Full-screen directory (snippets/index-directory.liquid rendered with
     context:'overlay'). Opened from the hero keycap and the filmstrip's own
     "View all" end tile (both carry data-ct-index-open); closed by its own
     close button, Escape, or a tile click (wired below, at gotoCatalogue). */
  var ctIndex = { overlay: null, lastFocus: null };
  function ctIndexKeydown(e) {
    if (e.key === 'Escape') { closeIndex(); return; }
    if (e.key !== 'Tab') return;
    var panel = ctIndex.overlay.querySelector('.ct-index-overlay__panel');
    var items = panel.querySelectorAll('a[href], button:not([disabled])');
    /* Both lens panels exist in the DOM at once (the inactive one only
       hidden via the `hidden` attribute), so an unfiltered query here would
       let the wrap target land on a tile that is not actually rendered or
       reachable by a real Tab press. offsetParent is null for anything
       inside a display:none subtree, so this keeps the trap to genuinely
       visible items only. */
    items = Array.prototype.filter.call(items, function (node) { return node.offsetParent !== null; });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }
  function openIndex(trigger) {
    ctIndex.overlay = document.querySelector('[data-ct-index]');
    if (!ctIndex.overlay) return;
    ctIndex.lastFocus = trigger || document.activeElement;
    ctIndex.overlay.hidden = false;
    requestAnimationFrame(function () { ctIndex.overlay.classList.add('is-open'); });
    document.documentElement.classList.add('ct-noscroll');
    ctIndex.overlay.querySelector('.ct-index-overlay__panel').focus();
    document.addEventListener('keydown', ctIndexKeydown);
  }
  function closeIndex() {
    if (!ctIndex.overlay) return;
    ctIndex.overlay.classList.remove('is-open');
    document.documentElement.classList.remove('ct-noscroll');
    document.removeEventListener('keydown', ctIndexKeydown);
    var o = ctIndex.overlay;
    /* Reduced motion never runs the fade (catalogue.css drops the transition
       to `none` there too), so hide immediately instead of waiting out a
       transition duration that will never actually play. */
    if (REDUCED) { o.hidden = true; } else { window.setTimeout(function () { o.hidden = true; }, 420); }
    if (ctIndex.lastFocus) ctIndex.lastFocus.focus();
  }
  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-ct-index-open]');
    if (open) { e.preventDefault(); openIndex(open); return; }
    if (e.target.closest('[data-ct-index-close]')) { e.preventDefault(); closeIndex(); }
  });
  /* Any bare "#the-index" anchor on a catalogue page opens the overlay
     instead of jumping (there is nothing at that hash to jump to). Rebound
     per catalogue.init() call (see the end of init()) so links inside
     freshly-swapped hero/gallery markup are picked up too. */
  function upgradeIndexLinks(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var links = root.querySelectorAll('a[href$="#the-index"]:not([data-ctidx-bound])');
    for (var i = 0; i < links.length; i++) {
      (function (a) {
        a.setAttribute('data-ctidx-bound', '1');
        a.addEventListener('click', function (e) { e.preventDefault(); openIndex(a); });
      })(links[i]);
    }
  }
  /* index-directory.liquid ships its own inline <script> to wire the
     Ranges|Categories tabs, which is enough for a normal page load. But a
     catalogue swap replaces the hero via cloneNode + replaceWith (see
     swapCataloguePage), and per the HTML spec, script elements inside a node
     inserted that way never execute. Re-run the identical binding here on
     every init() (guarded by the same data-ctidx-init attribute, so this is
     a no-op wherever the inline script already ran) so the tabs still work
     on every collection reached via the animated swap, not just a hard load. */
  function bindIndexTabs(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var groups = root.querySelectorAll('[data-ctidx]:not([data-ctidx-init])');
    for (var g = 0; g < groups.length; g++) {
      (function (idxRoot) {
        idxRoot.setAttribute('data-ctidx-init', '1');
        var tabs = idxRoot.querySelectorAll('[data-ctidx-tab]');
        for (var t = 0; t < tabs.length; t++) {
          (function (tab) {
            tab.addEventListener('click', function () {
              var name = tab.getAttribute('data-ctidx-tab');
              idxRoot.querySelectorAll('[data-ctidx-tab]').forEach(function (tEl) {
                var on = tEl === tab;
                tEl.classList.toggle('is-on', on);
                tEl.setAttribute('aria-pressed', String(on));
              });
              idxRoot.querySelectorAll('[data-ctidx-panel]').forEach(function (p) {
                p.hidden = p.getAttribute('data-ctidx-panel') !== name;
              });
            });
          })(tabs[t]);
        }
      })(groups[g]);
    }
  }

  var IronstoneCatalogue = {
    _io: null,
    _scroller: undefined,
    _raf: null,
    _rafFns: [],
    _pageCache: {},
    _swapping: false,

    scroller: function () {
      if (this._scroller === undefined) this._scroller = detectScroller();
      return this._scroller;
    },
    scrollTop: function () {
      var sc = this.scroller();
      return sc ? sc.scrollTop : (window.pageYOffset || document.documentElement.scrollTop);
    },
    setScrollTop: function (v) {
      var sc = this.scroller();
      if (sc) sc.scrollTop = v; else window.scrollTo(0, v);
    },

    /* One shared rAF loop; fns run while registered. */
    addTick: function (fn) {
      if (this._rafFns.indexOf(fn) === -1) this._rafFns.push(fn);
      if (!this._raf) this._startTicker();
    },
    removeTick: function (fn) {
      var i = this._rafFns.indexOf(fn);
      if (i > -1) this._rafFns.splice(i, 1);
    },
    _startTicker: function () {
      var self = this;
      var loop = function () {
        for (var i = 0; i < self._rafFns.length; i++) self._rafFns[i]();
        self._raf = self._rafFns.length ? requestAnimationFrame(loop) : null;
      };
      this._raf = requestAnimationFrame(loop);
    },
    stopTicks: function () {
      this._rafFns = [];
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    },

    sameOriginUrl: function (url) {
      try { return new URL(url, window.location.href).href; } catch (e) { return url; }
    },
    fetchCataloguePage: function (url) {
      var self = this;
      if (!window.fetch || !window.DOMParser) {
        return Promise.reject(new Error('Catalogue fetch APIs unavailable.'));
      }
      var href = this.sameOriginUrl(url);
      if (this._pageCache[href]) return this._pageCache[href];

      this._pageCache[href] = fetch(href, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Catalogue fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var heroSection = doc.querySelector('.ct-section-hero');
          var gallerySection = doc.querySelector('.ct-section-gallery');
          var hero = doc.querySelector('[data-ct-hero]');
          if (!heroSection || !gallerySection || !hero) {
            throw new Error('Catalogue sections missing from fetched page.');
          }
          return {
            title: doc.title,
            url: href,
            heroSection: heroSection,
            gallerySection: gallerySection
          };
        })
        .catch(function (err) {
          delete self._pageCache[href];
          throw err;
        });

      return this._pageCache[href];
    },
    prefetchCataloguePage: function (url) {
      if (!url) return;
      var self = this;
      try {
        var p = this.fetchCataloguePage(url);
        if (p && p.then) p = p.then(function (page) { return self.preloadCatalogueAssets(page); });
        if (p && p.catch) p.catch(function () {});
      } catch (e) {}
    },
    preloadImageNode: function (node) {
      return new Promise(function (resolve) {
        if (!node || !node.getAttribute('src')) {
          resolve();
          return;
        }
        var img = new Image();
        var done = function () { resolve(); };
        img.onload = done;
        img.onerror = done;
        if (node.getAttribute('sizes')) img.sizes = node.getAttribute('sizes');
        if (node.getAttribute('srcset')) img.srcset = node.getAttribute('srcset');
        img.src = node.getAttribute('src');
        if (img.decode) img.decode().then(done).catch(done);
        else if (img.complete) done();
      });
    },
    preloadCatalogueAssets: function (page) {
      if (!page || !page.heroSection) return Promise.resolve(page);
      var imgs = page.heroSection.querySelectorAll('.ct-hero__door');
      var jobs = [];
      for (var i = 0; i < imgs.length; i++) jobs.push(this.preloadImageNode(imgs[i]));
      return Promise.all(jobs).then(function () { return page; });
    },
    carryActiveVeilStyle: function (nextHeroSection, activeHero) {
      var nextHero = nextHeroSection && nextHeroSection.querySelector('[data-ct-hero]');
      var style = normalizeVeilStyle(activeHero && activeHero.dataset.ctVeilStyle);
      var activeVeil = activeHero && activeHero.querySelector('.ct-veil');
      if (!nextHero || !style || !activeVeil) return;

      var clonedVeil = activeVeil.cloneNode(true);
      var nextVeil = nextHero.querySelector('.ct-veil');
      var nextLines = nextHero.querySelectorAll('.ct-hero__headline--base span');
      var veilLines = clonedVeil.querySelectorAll('.ct-hero__headline--veil span');
      for (var i = 0; veilLines.length && nextLines.length && i < veilLines.length; i++) {
        veilLines[i].textContent = nextLines[i % nextLines.length].textContent;
      }

      if (nextVeil) nextVeil.replaceWith(clonedVeil);
      clonedVeil.classList.remove('ct-veil--curve', 'ct-veil--chevron', 'ct-veil--chevron-line', 'ct-veil--chevron-arc', 'ct-veil--chevron-arc-fill', 'ct-veil--frame', 'ct-veil--honeycomb', 'ct-veil--blinds');
      clonedVeil.classList.add('ct-veil--' + style);
      clonedVeil.setAttribute('data-ct-veil', style);
      nextHero.dataset.ctVeilStyle = style;
      nextHero.classList.remove('ct-hero--curve', 'ct-hero--chevron', 'ct-hero--chevron-line', 'ct-hero--chevron-arc', 'ct-hero--chevron-arc-fill', 'ct-hero--frame', 'ct-hero--honeycomb', 'ct-hero--blinds', 'ct-hero--solid');
      nextHero.classList.add('ct-hero--' + style);
      if (style !== 'curve' && style !== 'blinds' && !isChevronStyle(style)) nextHero.classList.add('ct-hero--solid');
    },
    killCatalogueTriggers: function () {
      if (!window.ScrollTrigger) return;
      var areas = document.querySelectorAll('.ct-section-hero, .ct-section-gallery, [data-ct-hero], [data-ct-gallery]');
      try {
        window.ScrollTrigger.getAll().forEach(function (trigger) {
          var t = trigger.trigger;
          if (!t) return;
          for (var i = 0; i < areas.length; i++) {
            if (areas[i] === t || areas[i].contains(t)) {
              trigger.kill();
              break;
            }
          }
        });
      } catch (e) {}
    },
    swapCataloguePage: function (page, dir) {
      var currentHeroSection = document.querySelector('.ct-section-hero');
      var currentGallerySection = document.querySelector('.ct-section-gallery');
      if (!currentHeroSection || !currentGallerySection) throw new Error('Current catalogue sections missing.');
      var activeHero = currentHeroSection.querySelector('[data-ct-hero]');

      var nextHeroSection = page.heroSection.cloneNode(true);
      var nextGallerySection = page.gallerySection.cloneNode(true);
      this.carryActiveVeilStyle(nextHeroSection, activeHero);

      this.stopTicks();
      this.killCatalogueTriggers();
      if (this._io && this._io.disconnect) this._io.disconnect();
      this._io = null;
      document.documentElement.classList.add('ct-arriving');
      try { sessionStorage.setItem('ct-arrive', dir || 'next'); } catch (e) {}
      this.setScrollTop(0);

      currentHeroSection.replaceWith(nextHeroSection);
      currentGallerySection.replaceWith(nextGallerySection);

      if (page.title) document.title = page.title;
      if (window.history && window.history.pushState) {
        window.history.pushState({ ctCatalogue: true }, page.title || '', page.url);
      }

      this._scroller = undefined;
      this.init(document);
    },

    revealObserver: function () {
      if (this._io) return this._io;
      this._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          IronstoneCatalogue._io.unobserve(e.target);
        });
      }, { root: this.scroller(), threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
      return this._io;
    },

    /* Smoothly tween the scroll position (the CTA path into the same
       scrubbed timeline). Cancels on any user input. */
    tweenScroll: function (target, dur) {
      var self = this;
      var from = this.scrollTop();
      var start = null;
      var cancelled = false;
      var cancel = function () { cancelled = true; cleanup(); };
      var opts = { passive: true };
      var cleanup = function () {
        window.removeEventListener('wheel', cancel, opts);
        window.removeEventListener('touchstart', cancel, opts);
        window.removeEventListener('keydown', cancel);
      };
      window.addEventListener('wheel', cancel, opts);
      window.addEventListener('touchstart', cancel, opts);
      window.addEventListener('keydown', cancel);
      var step = function (ts) {
        if (cancelled) return;
        if (start === null) start = ts;
        var p = Math.min(1, (ts - start) / dur);
        self.setScrollTop(from + (target - from) * easeMostlyLinear(p));
        if (window.ScrollTrigger) {
          try { window.ScrollTrigger.update(); } catch (e) {}
        }
        if (p < 1) requestAnimationFrame(step); else cleanup();
      };
      requestAnimationFrame(step);
    },

    init: function (scope) {
      var root = scope && scope.querySelector ? scope : document;
      if (hasST()) {
        try {
          window.gsap.registerPlugin(window.ScrollTrigger);
          var sc = this.scroller();
          if (sc) window.ScrollTrigger.defaults({ scroller: sc });
        } catch (e) {}
      }
      var heroes = root.querySelectorAll('[data-ct-hero]');
      var galleries = root.querySelectorAll('[data-ct-gallery]');
      for (var i = 0; i < heroes.length; i++) this.initHero(heroes[i]);
      for (var j = 0; j < galleries.length; j++) this.initGallery(galleries[j]);
      if (hasST()) { try { window.ScrollTrigger.refresh(); } catch (e) {} }
      /* Filmstrip: centre the current chip inside the masked film strip.
         Runs on first load AND after every catalogue swap (swapCataloguePage
         calls init(document) again on the freshly-swapped markup), so the
         strip always opens re-centred on the collection you just landed on.
         Query through `root` (this call's own scope), not a bare `document`
         lookup, so this stays correct if init() is ever invoked on a scoped
         subtree rather than the whole document.
         Set film.scrollLeft directly instead of chip.scrollIntoView(): the
         brief's inline:'center', block:'nearest' actually moved the whole
         PAGE, not just the strip -- "nearest" still permits scrolling an
         ancestor when the element isn't fully in view, and the film has no
         vertical overflow of its own, so the scroll escalated to the window.
         Scrolling only the film's own horizontal axis can never move the page. */
      var curChip = root.querySelector('.ct-strip__chip.is-current');
      var film = root.querySelector('[data-ct-film]');
      if (curChip && film) {
        /* offsetLeft is relative to the nearest positioned offsetParent, which
           is NOT necessarily the film (it has no position set), so it does not
           reliably measure "distance from the film's scrollable start". Use
           getBoundingClientRect deltas instead: always viewport-accurate
           regardless of the offsetParent chain, and correct however far the
           film is already scrolled. */
        var fr = film.getBoundingClientRect();
        var cr = curChip.getBoundingClientRect();
        film.scrollLeft += (cr.left + cr.width / 2) - (fr.left + fr.width / 2);
      }
      upgradeIndexLinks(root);
      bindIndexTabs(root);
    },

    /* ================= HERO ================= */
    initHero: function (el) {
      if (!el || el.dataset.ctBound === '1') return;
      el.dataset.ctBound = '1';

      var self = this;
      var stage = el.querySelector('[data-ct-stage]');
      var norun = REDUCED || !hasST();

      /* Every piece of hero copy fades together on a transition, so nothing
         snaps on or off while the veil covers or peels. */
      var CHROME_SEL = '.ct-hero__headline--base, .ct-hero__headline--veil, .ct-hero__headline--arcveil, .ct-hero__eyebrow, .ct-hero__marker, .ct-hero__note, .ct-hero__bar, .ct-nextcat, .ct-hero__scrollhint';

      /* Chevron line states are pure transforms. The two fixed diagonal panels
         travel along their own normals; no shared vertex is ever animated. */
      var chevronMoveState = function (mode) {
        var w = stage ? stage.offsetWidth : window.innerWidth;
        var h = stage ? stage.offsetHeight : window.innerHeight;
        if (mode === 'cover') {
          return {
            top: { x: w * 0.76, y: h * 0.54 },
            bottom: { x: w * 0.76, y: h * 0.40 }
          };
        }
        return {
          top: { x: w * -0.52, y: h * -0.46 },
          bottom: { x: w * -0.54, y: h * 0.42 }
        };
      };

      /* Expand the current veil until it covers the hero stage (never the
         header): the exact opposite of the scroll-down peel. */
      var coverVeil = function (onDone) {
        var gsap = window.gsap;
        var veilEl = el.querySelector('.ct-veil');
        var plateEl = el.querySelector('[data-ct-plate]');
        var frameEl = el.querySelector('[data-ct-frame]');
        var sheetEl = el.querySelector('[data-ct-sheet]');
        var chevFill = el.querySelector('[data-ct-chevron-fill]');
        var chevTop = el.querySelector('[data-ct-chevron-panel="top"]');
        var chevBottom = el.querySelector('[data-ct-chevron-panel="bottom"]');
        var arcShapes = el.querySelectorAll('[data-ct-arcshape]');
        var mobileArcShape = el.querySelector('[data-ct-arc-mobile]');
        var arcSweep = el.querySelector('[data-ct-arc-sweep]');
        var blindsWrap = el.querySelector('[data-ct-blinds]');
        var slats = el.querySelectorAll('[data-ct-slat]');
        if (!veilEl || !gsap) { onDone(); return; }
        veilEl.style.zIndex = 8;
        /* All hero copy dissolves with the cover, never a hard cut. */
        var chromeFade = el.querySelectorAll(CHROME_SEL);
        if (chromeFade.length) gsap.to(chromeFade, { autoAlpha: 0, duration: 0.36, ease: 'power2.out' });
        var opts = { duration: 0.75, ease: 'power2.inOut', onComplete: onDone };
        var activeStyle = normalizeVeilStyle(el.dataset.ctVeilStyle);
        if (activeStyle === 'chevron-line' && chevTop && chevBottom) {
          var cState = chevronMoveState('cover');
          var chevTl = gsap.timeline({ onComplete: onDone });
          if (plateEl) chevTl.to(plateEl, { x: 0, y: 0, duration: 0.01 }, 0);
          if (chevFill) chevTl.to(chevFill, { scaleX: 1, duration: 0.62, ease: 'power2.inOut' }, 0.10);
          chevTl.to(chevTop, {
            x: function () { return cState.top.x; },
            y: function () { return cState.top.y; },
            duration: 0.75,
            ease: 'power2.inOut'
          }, 0);
          chevTl.to(chevBottom, {
            x: function () { return cState.bottom.x; },
            y: function () { return cState.bottom.y; },
            duration: 0.75,
            ease: 'power2.inOut'
          }, 0);
        } else if (activeStyle === 'chevron-arc-fill' && arcShapes.length) {
          /* Desktop and mobile have independent resting silhouettes, but one
             transition contract: both paths morph to a full rectangle. Keeping
             the hidden drawing in sync also makes breakpoint resizes safe. */
          var arcFills = [];
          for (var af = 0; af < arcShapes.length; af++) {
            var arcFillFrom = arcRestNums(arcShapes[af]);
            var arcFillPath = arcShapes[af].querySelector('path');
            if (arcFillFrom && arcFillPath) {
              arcFills.push({ path: arcFillPath, from: arcFillFrom, to: arcFull(arcFillFrom) });
            }
          }
          /* If a mid-scroll cover fires, the scroll timeline may have faded the
             arcs out; make sure both are ready before the visible one expands. */
          gsap.set(arcShapes, { autoAlpha: 1 });
          if (arcFills.length) {
            var fillProxy = { t: 0 };
            var fillTl = gsap.timeline({ onComplete: onDone });
            fillTl.to(fillProxy, {
              t: 1, duration: 0.75, ease: 'power2.inOut',
              onUpdate: function () {
                for (var afi = 0; afi < arcFills.length; afi++) {
                  arcMorph(arcFills[afi].path, arcFills[afi].from, arcFills[afi].to, fillProxy.t);
                }
              }
            }, 0);
            /* At rest the mobile drawing is cropped into a deliberate canopy.
               Reveal its hidden return leg while it grows so it seals the same
               full hero stage as the desktop veil. */
            if (mobileArcShape) {
              fillTl.to(mobileArcShape, { clipPath: 'inset(0 0 0% 0)', duration: 0.75, ease: 'power2.inOut' }, 0);
            }
          } else { onDone(); }
        } else if (activeStyle === 'chevron-arc' && arcSweep) {
          /* The ink blooms out of the pivot as a growing arc; the arrow
             pivots aside and dissolves into it (a plain scale would only
             blow up the concave notch and leave the door uncovered). */
          var arcTl = gsap.timeline({ onComplete: onDone });
          arcTl.fromTo(arcSweep, { '--arc': 0 }, { '--arc': 1, duration: 0.72, ease: 'power2.inOut' }, 0);
          if (arcShapes.length) arcTl.to(arcShapes, { rotation: -7, scale: 1.12, transformOrigin: '6% 58%', autoAlpha: 0, duration: 0.5, ease: 'power2.in' }, 0);
        } else if (activeStyle === 'blinds' && slats.length) {
          /* The louvers flip flat (closing their gaps) while the band widens
             to the whole frame: the blinds shut over the hero. */
          if (blindsWrap) blindsWrap.classList.add('ct-blinds--still');
          var blTl = gsap.timeline({ onComplete: onDone });
          if (blindsWrap) blTl.fromTo(blindsWrap, { '--blind-open': 0.56 }, { '--blind-open': 0, duration: 0.72, ease: 'power2.inOut' }, 0);
          blTl.to(slats, { rotationY: 0, duration: 0.62, ease: 'power3.inOut', stagger: { each: 0.03, from: 'start' } }, 0);
        } else if (plateEl) {
          gsap.to(plateEl, Object.assign({ scale: 2.4, x: 0, y: 0 }, opts));
        } else if (frameEl) {
          gsap.to(frameEl, Object.assign({ scale: 4.2, xPercent: 0, yPercent: 0 }, opts));
        } else if (sheetEl) {
          gsap.to(sheetEl, Object.assign({ scaleX: 1 }, opts));
        } else {
          onDone();
        }
      };

      /* Collection cycling data (present when the ring has 2+ entries). */
      var ring = el.dataset.ctNextUrl ? {
        next: { url: el.dataset.ctNextUrl, name: el.dataset.ctNextName, accent: el.dataset.ctNextAccent },
        prev: { url: el.dataset.ctPrevUrl, name: el.dataset.ctPrevName, accent: el.dataset.ctPrevAccent }
      } : null;

      var cycling = false;

      /* The commit: cover the hero with the veil, fetch + swap in the next
         catalogue. Assumes the hero is already parked at its resting state. */
      var doCycle = function (dir, to) {
        self._swapping = true;
        var pageReady = self.fetchCataloguePage(to.url).then(function (page) {
          return self.preloadCatalogueAssets(page);
        }).catch(function (err) {
          return { error: err };
        });
        coverVeil(function () {
          pageReady.then(function (page) {
            if (page && !page.error) {
              try {
                self.swapCataloguePage(page, dir);
                cycling = false;
                self._swapping = false;
                return;
              } catch (e) {}
            }
            try { sessionStorage.setItem('ct-arrive', dir); } catch (e) {}
            window.location.href = to.url;
          });
        });
      };

      /* If the visitor has scrolled down into the zoomed product / gallery, run
         the "view the collection" choreography IN REVERSE first: glide the
         scroll back up to the hero's resting state while rewinding the veil +
         product zoom (decoupled from the scrub, same trapezoid ease as the CTA),
         THEN commit the cover + swap. So the next catalogue is always entered
         from the very top of its hero, exactly as it is meant to be seen. */
      var returnToRestThen = function (cb) {
        var st = (typeof tl !== 'undefined' && tl) ? tl.scrollTrigger : null;
        if (!window.gsap || !st || self.scrollTop() <= 4) { cb(); return; }
        var behaviorTargets = [self.scroller(), document.scrollingElement, document.documentElement, document.body].filter(Boolean);
        var prevBehavior = behaviorTargets.map(function (n) { return n.style.scrollBehavior; });
        behaviorTargets.forEach(function (n) { n.style.scrollBehavior = 'auto'; });
        var restoreBehavior = function () {
          behaviorTargets.forEach(function (n, i) { n.style.scrollBehavior = prevBehavior[i] || ''; });
        };
        var proxy = { p: st.progress, s: self.scrollTop() };
        var dur = Math.max(0.5, Math.min(0.95, 0.5 + proxy.s / 6000));
        st.disable(false);
        window.gsap.to(proxy, {
          p: 0, s: 0, duration: dur, ease: trapEase,
          onUpdate: function () {
            tl.progress(proxy.p);
            self.setScrollTop(proxy.s);
          },
          onComplete: function () {
            tl.progress(0);
            self.setScrollTop(0);
            restoreBehavior();
            cb();
          }
        });
      };

      var cycle = function (dir) {
        if (!ring || cycling || self._swapping) return;
        var to = ring[dir] || ring.next;
        if (norun || !window.gsap) {
          window.location.href = to.url;
          return;
        }
        cycling = true;
        returnToRestThen(function () { doCycle(dir, to); });
      };

      /* Wire the cycling triggers (work in every mode, incl. norun). */
      var cyclers = el.querySelectorAll('[data-ct-cycle]');
      for (var c = 0; c < cyclers.length; c++) {
        (function (btn) {
          btn.addEventListener('click', function () { cycle(btn.dataset.ctCycle); });
        })(cyclers[c]);
      }

      /* Direct jumps from the filmstrip chips + the lens toggle: run the SAME
         return-to-rest + cover + swap as the keycap, to an explicit target URL,
         so nothing in the strip ever hard-jumps or instant-loads. */
      var gotoCatalogue = function (to, dir) {
        if (cycling || self._swapping || !to || !to.url) return;
        if (norun || !window.gsap) { window.location.href = to.url; return; }
        cycling = true;
        returnToRestThen(function () { doCycle(dir || 'next', to); });
      };
      var gotos = el.querySelectorAll('[data-ct-goto]');
      for (var gi = 0; gi < gotos.length; gi++) {
        (function (a) {
          a.addEventListener('click', function (ev) {
            /* Let modified clicks (new tab / download) behave normally. */
            if (ev.defaultPrevented || ev.button || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
            ev.preventDefault();
            /* A tile inside the INDEX overlay: run the SAME animated swap as
               every other data-ct-goto target, then close the overlay so the
               visitor lands on the arriving hero, not a dialog on top of it. */
            var fromIndex = !!a.closest('[data-ct-index]');
            gotoCatalogue({ url: a.getAttribute('data-ct-goto') }, a.getAttribute('data-ct-goto-dir'));
            if (fromIndex) closeIndex();
          });
        })(gotos[gi]);
      }
      if (ring) {
        window.addEventListener('keydown', function (ev) {
          if (ev.defaultPrevented) return;
          var tag = (document.activeElement && document.activeElement.tagName) || '';
          if (/INPUT|TEXTAREA|SELECT/.test(tag)) return;
          var r = stage ? stage.getBoundingClientRect() : null;
          if (!r || r.bottom < 80 || r.top > window.innerHeight - 80) return;
          if (ev.key === 'ArrowRight') cycle('next');
          if (ev.key === 'ArrowLeft') cycle('prev');
        });
        if (stage) {
          var px = null, py = null;
          stage.addEventListener('pointerdown', function (ev) {
            px = ev.clientX; py = ev.clientY;
          }, { passive: true });
          stage.addEventListener('pointerup', function (ev) {
            if (px === null) return;
            var dx = ev.clientX - px;
            var dy = ev.clientY - py;
            px = py = null;
            if (Math.abs(dx) > 70 && Math.abs(dx) > 2 * Math.abs(dy)) {
              cycle(dx < 0 ? 'next' : 'prev');
            }
          }, { passive: true });
        }
        /* Prefetch the neighbours once the page settles. */
        var prefetch = function () {
          ['next', 'prev'].forEach(function (k) {
            var url = ring[k] && ring[k].url;
            if (!url || document.querySelector('link[data-ct-prefetch="' + url + '"]')) return;
            var l = document.createElement('link');
            l.rel = 'prefetch';
            l.href = url;
            l.setAttribute('data-ct-prefetch', url);
            document.head.appendChild(l);
            self.prefetchCataloguePage(url);
          });
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(prefetch, { timeout: 1200 });
        else window.setTimeout(prefetch, 800);
      }

      /* CTA: run the exact same choreography as scrolling, then land on the
         gallery. The scrubbed timeline lags the scroll (that lag is what made
         earlier attempts crawl then snap), so for the CTA we DECOUPLE: disable
         the scrub, drive the timeline playhead AND the scroll position together
         on one trapezoid ease (fast ramp, linear middle, fast ease-out), then
         re-arm the scrub at the final position. No lag => uniform motion. */
      var enter = el.querySelector('[data-ct-enter]');
      if (enter) {
        enter.addEventListener('click', function (ev) {
          var gallery = document.getElementById('ct-collection');
          if (!gallery) return;
          ev.preventDefault();
          var st = (typeof tl !== 'undefined' && tl) ? tl.scrollTrigger : null;
          if (norun || !window.gsap || !st) {
            gallery.scrollIntoView({ behavior: 'smooth' });
            return;
          }
          if (self._ctaPlaying) return;
          self._ctaPlaying = true;

          /* CSS scroll-behavior:smooth (Horizon sets it) turns every per-frame
             scrollTop write into its own easing animation, so the tween crawls
             then the last write settles late: THAT is the slow-ramp-then-jump.
             Force instant scrolling for the duration, restore it after. */
          var behaviorTargets = [self.scroller(), document.scrollingElement, document.documentElement, document.body].filter(Boolean);
          var prevBehavior = behaviorTargets.map(function (n) { return n.style.scrollBehavior; });
          behaviorTargets.forEach(function (n) { n.style.scrollBehavior = 'auto'; });

          var stageH = stage ? stage.offsetHeight : window.innerHeight;
          var heroTop = el.getBoundingClientRect().top + self.scrollTop();
          var target = heroTop + el.offsetHeight - stageH;
          var proxy = { p: st.progress, s: self.scrollTop() };
          var done = false;

          var restoreBehavior = function () {
            behaviorTargets.forEach(function (n, i) { n.style.scrollBehavior = prevBehavior[i] || ''; });
          };
          var cleanup = function () {
            window.removeEventListener('wheel', onInput, listenerOpts);
            window.removeEventListener('touchstart', onInput, listenerOpts);
            window.removeEventListener('keydown', onInput);
          };
          var rearm = function (scrollTo) {
            self.setScrollTop(scrollTo);
            try { st.enable(); } catch (e) {}
            try { st.scroll(self.scrollTop()); } catch (e) {}
            if (window.ScrollTrigger) { try { window.ScrollTrigger.update(); } catch (e) {} }
            restoreBehavior();
            cleanup();
            self._ctaPlaying = false;
          };
          var listenerOpts = { passive: true };
          var onInput = function () {
            if (done) return;
            done = true;
            gsap.killTweensOf(proxy);
            rearm(self.scrollTop()); /* the visitor took over: stay put */
          };

          window.addEventListener('wheel', onInput, listenerOpts);
          window.addEventListener('touchstart', onInput, listenerOpts);
          window.addEventListener('keydown', onInput);

          st.disable(false);
          gsap.to(proxy, {
            p: 1,
            s: target,
            duration: 1.05,
            ease: trapEase,
            onUpdate: function () {
              tl.progress(proxy.p);
              self.setScrollTop(proxy.s);
            },
            onComplete: function () {
              if (done) return;
              done = true;
              tl.progress(1);
              rearm(target);
            }
          });
        });
      }

      if (norun) {
        el.classList.add('ct-hero--norun');
        document.documentElement.classList.remove('ct-arriving');
        try { sessionStorage.removeItem('ct-arrive'); } catch (e) {}
        return;
      }

      /* Runtime is live: unlock the tall scroll run + the sheet overlap. */
      document.documentElement.classList.add('ct-run');

      var gsap = window.gsap;
      var style = normalizeVeilStyle(el.dataset.ctVeilStyle);
      el.dataset.ctVeilStyle = style;
      var plate = el.querySelector('[data-ct-plate]');
      var frame = el.querySelector('[data-ct-frame]');
      var baseHead = el.querySelector('.ct-hero__headline--base');
      var veilHeads = el.querySelectorAll('.ct-hero__headline--veil');
      var arcVeilHead = el.querySelector('.ct-hero__headline--arcveil');
      var door = el.querySelector('.ct-hero__door');
      var doorwrap = el.querySelector('[data-ct-doorwrap]');
      var shadow = el.querySelector('.ct-hero__shadow');
      var cells = el.querySelectorAll('.ct-veil__cell');
      var chrome = el.querySelectorAll('.ct-hero__eyebrow, .ct-hero__marker, .ct-hero__note, .ct-hero__bar, .ct-hero__scrollhint');
      var headSpans = el.querySelectorAll('.ct-hero__headline span');
      var chevFill = el.querySelector('[data-ct-chevron-fill]');
      var chevTop = el.querySelector('[data-ct-chevron-panel="top"]');
      var chevBottom = el.querySelector('[data-ct-chevron-panel="bottom"]');
      var arcShapes = el.querySelectorAll('[data-ct-arcshape]');
      var desktopArcShape = el.querySelector('.ct-veil__arcshape--desktop');
      var mobileArcShape = el.querySelector('[data-ct-arc-mobile]');
      var arcSweep = el.querySelector('[data-ct-arc-sweep]');
      var blindsWrap = el.querySelector('[data-ct-blinds]');
      var slats = el.querySelectorAll('[data-ct-slat]');
      var introDone = false;

      /* Manual two-tone (chevron arc): the white over-veil copy stays put, and
         only its arc CLIP tracks the veil ink as the ink recedes on scroll, so
         the colour split follows the ink instead of freezing at the rest edge
         (a static clip leaves the left text white-on-white / teal-on-teal).
         Mirror the ink's LIVE transform matrix into the clip, converting its
         pixel translation into the clip's 0..1 objectBoundingBox space (the box
         is the square stage width). The copy is never moved, so the base copy is
         never doubled. Auto mode hides the copy, so this is a no-op there. */
      var arcClipPath = el.querySelector('.ct-arc-clipdef clipPath path');
      var arcClipStage = el.querySelector('.ct-hero__stage');
      var syncArcClip = function () {
        if (!arcClipPath || !desktopArcShape) return;
        var cs = window.getComputedStyle(desktopArcShape).transform;
        if (!cs || cs === 'none') { arcClipPath.removeAttribute('transform'); return; }
        var mm = cs.match(/matrix\(([^)]+)\)/);
        if (!mm) return;
        var p = mm[1].split(',');
        var boxW = (arcVeilHead && arcVeilHead.offsetWidth) || (arcClipStage && arcClipStage.offsetWidth) || 1;
        var boxH = (arcVeilHead && arcVeilHead.offsetHeight) || boxW;
        arcClipPath.setAttribute('transform', 'matrix(' + p[0] + ' ' + p[1] + ' ' + p[2] + ' ' + p[3] + ' ' + (parseFloat(p[4]) / boxW) + ' ' + (parseFloat(p[5]) / boxH) + ')');
      };

      /* Arrival from a swipe: the veil renders COVERING the hero (class set
         pre-paint in the head), now peel it back to its composed position:
         the mirror of the exit on the previous page. */
      var arriving = document.documentElement.classList.contains('ct-arriving');
      if (arriving) {
        introDone = true;
        try { sessionStorage.removeItem('ct-arrive'); } catch (e) {}
        var veilEl = el.querySelector('.ct-veil');
        var sheetEl = el.querySelector('[data-ct-sheet]');
        if (blindsWrap) blindsWrap.classList.add('ct-blinds--still');
        if (headSpans.length) gsap.set(headSpans, { yPercent: 0 });
        if (door) gsap.set(door, { autoAlpha: 1, y: 0, scale: 1 });
        if (doorwrap) gsap.set(doorwrap, { x: 0 });
        if (shadow) gsap.set(shadow, { autoAlpha: 1 });
        if (cells.length) gsap.set(cells, { autoAlpha: 1, y: 0 });

        /* Hero copy fades back in with the peel, mirroring the exit. */
        var arriveChrome = el.querySelectorAll(CHROME_SEL);
        if (arriveChrome.length) {
          gsap.set(arriveChrome, { autoAlpha: 0, y: 0 });
          gsap.to(arriveChrome, { autoAlpha: 1, duration: 0.62, ease: 'power2.out', delay: 0.3 });
        }

        var arriveDone = function () {
          document.documentElement.classList.remove('ct-arriving');
          if (veilEl) { veilEl.style.zIndex = ''; veilEl.style.background = ''; }
          if (plate) gsap.set(plate, { clearProps: 'transform,clipPath' });
          if (chevFill) gsap.set(chevFill, { clearProps: 'transform' });
          if (chevTop) gsap.set(chevTop, { clearProps: 'transform,clipPath' });
          if (chevBottom) gsap.set(chevBottom, { clearProps: 'transform,clipPath' });
          if (arcShapes.length) gsap.set(arcShapes, { clearProps: 'transform,opacity,clipPath' });
          if (arcSweep) gsap.set(arcSweep, { '--arc': 0 });
          if (frame) gsap.set(frame, { clearProps: 'transform' });
          if (sheetEl) gsap.set(sheetEl, { clearProps: 'transform' });
          if (slats.length) gsap.set(slats, { clearProps: 'transform,opacity' });
          if (blindsWrap) {
            gsap.set(blindsWrap, { '--blind-open': 0.56 });
            if (self.scrollTop() < 4) blindsWrap.classList.remove('ct-blinds--still');
          }
        };
        var aOpts = { duration: 0.6, ease: 'power2.out', onComplete: arriveDone };
        if (style === 'chevron-line' && chevTop && chevBottom) {
          var cState = chevronMoveState('cover');
          var arriveTl = gsap.timeline({ onComplete: arriveDone });
          if (chevFill) arriveTl.fromTo(chevFill, { scaleX: 1 }, { scaleX: 0, duration: 0.44, ease: 'power2.out' }, 0.04);
          arriveTl.fromTo(chevTop, { x: cState.top.x, y: cState.top.y }, { x: 0, y: 0, duration: 0.6, ease: 'power2.out' }, 0);
          arriveTl.fromTo(chevBottom, { x: cState.bottom.x, y: cState.bottom.y }, { x: 0, y: 0, duration: 0.6, ease: 'power2.out' }, 0);
        } else if (style === 'chevron-arc-fill' && arcShapes.length) {
          /* Peel the full ink back to the arrow. Previously the veil stayed ABOVE
             the product for the whole peel (z-index 8) and only snapped BEHIND it
             at rest (z-index 3), a visible z-order jump on the product's left edge.
             Instead, drop the veil to its resting layer (behind the product) right
             away, and hold the product hidden for the single covered frame, fading
             it in as the ink recedes behind it. The ink now always peels behind the
             product, so there is no jump. */
          var arcIns = [];
          for (var ai = 0; ai < arcShapes.length; ai++) {
            var arcInFrom = arcRestNums(arcShapes[ai]);
            var arcInPath = arcShapes[ai].querySelector('path');
            if (arcInFrom && arcInPath) {
              var arcInTo = arcFull(arcInFrom);
              arcMorph(arcInPath, arcInFrom, arcInTo, 1);
              arcIns.push({ path: arcInPath, from: arcInFrom, to: arcInTo });
            }
          }
          if (arcIns.length) {
            if (mobileArcShape) gsap.set(mobileArcShape, { clipPath: 'inset(0 0 0% 0)' });
            if (veilEl) { veilEl.style.background = 'transparent'; veilEl.style.zIndex = '3'; }
            if (door) gsap.set(door, { autoAlpha: 0 });
            if (shadow) gsap.set(shadow, { autoAlpha: 0 });
            if (door) gsap.to(door, { autoAlpha: 1, duration: 0.45, ease: 'power2.out' });
            if (shadow) gsap.to(shadow, { autoAlpha: 1, duration: 0.5, ease: 'power2.out', delay: 0.12 });
            var arcInProxy = { t: 1 };
            var arcInTl = gsap.timeline({ onComplete: arriveDone });
            arcInTl.to(arcInProxy, {
              t: 0, duration: 0.62, ease: 'power2.out',
              onUpdate: function () {
                for (var aii = 0; aii < arcIns.length; aii++) {
                  arcMorph(arcIns[aii].path, arcIns[aii].from, arcIns[aii].to, arcInProxy.t);
                }
              }
            }, 0);
            if (mobileArcShape) {
              arcInTl.to(mobileArcShape, { clipPath: mobileArcRest(), duration: 0.62, ease: 'power2.out' }, 0);
            }
          } else { arriveDone(); }
        } else if (style === 'chevron-arc' && arcSweep) {
          /* The bloom recedes back into the pivot; the arrow pivots home. */
          var arcInTl = gsap.timeline({ onComplete: arriveDone });
          arcInTl.fromTo(arcSweep, { '--arc': 1 }, { '--arc': 0, duration: 0.6, ease: 'power2.out' }, 0);
          if (arcShapes.length) arcInTl.fromTo(arcShapes, { autoAlpha: 0, rotation: -7, scale: 1.12, transformOrigin: '6% 58%' }, { autoAlpha: 1, rotation: 0, scale: 1, duration: 0.55, ease: 'power2.out' }, 0.16);
        } else if (style === 'blinds' && slats.length) {
          /* The louvers flip open + the band retracts, revealing the hero. */
          var blInTl = gsap.timeline({ onComplete: arriveDone });
          if (blindsWrap) blInTl.fromTo(blindsWrap, { '--blind-open': 0 }, { '--blind-open': 0.56, duration: 0.6, ease: 'power2.out' }, 0);
          blInTl.fromTo(slats, { rotationY: 0 }, { rotationY: 46, duration: 0.58, ease: 'power3.out', stagger: { each: 0.028, from: 'start' } }, 0.05);
        } else if (plate) {
          gsap.fromTo(plate, { scale: 2.4 }, Object.assign({ scale: 1 }, aOpts));
        } else if (frame) {
          gsap.fromTo(frame, { scale: 4.2 }, Object.assign({ scale: 1 }, aOpts));
        } else if (sheetEl) {
          gsap.fromTo(sheetEl, { scaleX: 1 }, Object.assign({ scaleX: 0 }, aOpts));
        } else {
          arriveDone();
        }
      }

      /* Entrance (plays once; a scroll mid-way jumps it to done). */
      var intro = null;
      if (!arriving) {
        if (blindsWrap) blindsWrap.classList.add('ct-blinds--still');
        intro = gsap.timeline({
          delay: 0.1,
          onComplete: function () {
            introDone = true;
            if (blindsWrap && self.scrollTop() < 4) blindsWrap.classList.remove('ct-blinds--still');
          }
        });
        if (headSpans.length) {
          intro.fromTo(headSpans, { yPercent: 118 }, { yPercent: 0, duration: 1.05, ease: 'power4.out', stagger: 0.08 }, 0.05);
        }
        /* The product's fade waits for its own PIXELS, not for the page.

           It used to start with everything else on page load. On a cold cache
           that meant watching an empty space fade gracefully in, and then the
           render snapping on top of it once it arrived. That snap is what read
           as "slow": the file is only ~170KB at normal screen sizes, so the
           problem was never weight, it was that the choreography and the
           download were not talking to each other.

           A cached image is already complete by the time this runs, so it stays
           in the intro timeline exactly as before and nothing changes for repeat
           visits. Only a genuinely-still-loading image gets the deferred path,
           and that path is capped so a stalled or broken image can never leave
           the hero empty. */
        if (door) {
          var doorFromTo = { y: 26, autoAlpha: 0 };
          var doorTo = { y: 0, autoAlpha: 1, duration: 1.15, ease: 'power3.out' };
          if (door.complete && door.naturalWidth > 0) {
            intro.fromTo(door, doorFromTo, doorTo, 0);
          } else {
            gsap.set(door, doorFromTo);
            var doorRevealed = false;
            var revealDoor = function () {
              if (doorRevealed) return;
              doorRevealed = true;
              gsap.to(door, Object.assign({ overwrite: 'auto' }, doorTo));
            };
            door.addEventListener('load', revealDoor, { once: true });
            door.addEventListener('error', revealDoor, { once: true });
            /* Safety net: never hold the hero hostage to a request that hangs. */
            setTimeout(revealDoor, 4000);
          }
        }
        if (shadow) intro.fromTo(shadow, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1 }, 0.25);
        if (plate) {
          intro.fromTo(plate, { x: -56, y: -44 }, { x: 0, y: 0, duration: 1.1, ease: 'power3.out' }, 0);
          if (veilHeads.length) intro.fromTo(veilHeads, { x: 56, y: 44 }, { x: 0, y: 0, duration: 1.1, ease: 'power3.out' }, 0);
        }
        if (frame) intro.fromTo(frame, { xPercent: -8, autoAlpha: 0 }, { xPercent: 0, autoAlpha: 1, duration: 1.1, ease: 'power3.out' }, 0);
        if (slats.length) intro.fromTo(slats, { autoAlpha: 0, rotationY: 78 }, { autoAlpha: 1, rotationY: 46, duration: 1.0, ease: 'power3.out', stagger: 0.045 }, 0.12);
        if (cells.length) intro.fromTo(cells, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, stagger: 0.012, ease: 'power1.out' }, 0.15);
        if (chrome.length) intro.fromTo(chrome, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.05, ease: 'power2.out' }, 0.45);
      } else {
        introDone = true;
      }

      /* Scrubbed master timeline. Plate + white headline copy move by the
         same eased amounts in opposite directions, so the split headline
         stays ONE text whose colour flips at the plate edge. */
      var tl = gsap.timeline({
        onUpdate: syncArcClip,
        scrollTrigger: {
          trigger: el,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.18,
          invalidateOnRefresh: true,
          onUpdate: function (stSelf) {
            if (intro && intro.isActive() && stSelf.progress > 0.02) intro.progress(1);
            if (blindsWrap) blindsWrap.classList.toggle('ct-blinds--still', stSelf.progress > 0.003 || !introDone);
          }
        }
      });

      var plateShift = function () {
        if (isChevronStyle(style)) return -((stage ? stage.offsetWidth : window.innerWidth) * 0.92);
        return -((plate ? plate.offsetWidth : 0) + (stage ? stage.offsetWidth * 0.06 : 80));
      };
      var plateLift = function () {
        if (isChevronStyle(style)) return 0;
        return -((stage ? stage.offsetHeight : window.innerHeight) * 0.34);
      };
      /* Veil exit (0 to ~0.30). Curve acts as one plate; chevron line panels
         keep their shape and translate along their separate normals. The white
         copy fades so no fragment lingers once the ink has cleared. */
      if (style === 'chevron-line' && chevTop && chevBottom) {
        tl.to(chevTop, {
          x: function () { return chevronMoveState('clear').top.x; },
          y: function () { return chevronMoveState('clear').top.y; },
          ease: 'power1.in',
          duration: 0.30
        }, 0);
        tl.to(chevBottom, {
          x: function () { return chevronMoveState('clear').bottom.x; },
          y: function () { return chevronMoveState('clear').bottom.y; },
          ease: 'power1.in',
          duration: 0.30
        }, 0);
        tl.to([chevTop, chevBottom], { autoAlpha: 0, duration: 0.06, ease: 'none' }, 0.24);
        if (veilHeads.length) {
          tl.to(veilHeads, { autoAlpha: 0, duration: 0.12, ease: 'none' }, 0.16);
        }
      } else if (isArcStyle(style) && arcShapes.length) {
        tl.to(arcShapes, {
          xPercent: -92,
          yPercent: -26,
          rotation: -10,
          ease: 'power1.in',
          duration: 0.30
        }, 0);
        /* Translation alone can't clear shapes whose bottom arm reaches past 92%
           (wide x4=98, modern x4=100, more at higher coverage): the tip lands
           back on-screen. Fade the residual out like the chevron-line branch so
           no dark sliver lingers over the white canvas. Reverse restores it. */
        tl.to(arcShapes, { autoAlpha: 0, duration: 0.08, ease: 'none' }, 0.24);
        /* The copy stays put (no y drift) so its arc clip, mirrored from the ink
           above, keeps the split aligned to the receding ink while it fades. */
        if (arcVeilHead) tl.to(arcVeilHead, { autoAlpha: 0, duration: 0.16, ease: 'power1.out' }, 0.12);
      } else if (style === 'blinds' && slats.length) {
        /* The louvers flip edge-on while the whole band (slats + wash) fades,
           clearing to the filling product. immediateRender:false keeps the rest
           angle (46deg) as the baseline so the CSS wind, not this tween, owns
           the slats until you actually scroll. */
        tl.fromTo(slats, { rotationY: 46 }, {
          rotationY: 92,
          ease: 'power1.in',
          duration: 0.30,
          stagger: { each: 0.006, from: 'start' },
          immediateRender: false
        }, 0);
        if (blindsWrap) tl.to(blindsWrap, { autoAlpha: 0, duration: 0.22, ease: 'power1.out' }, 0.08);
      } else if (plate) {
        tl.to(plate, { x: plateShift, y: plateLift, ease: 'power1.in', duration: 0.30 }, 0);
        if (veilHeads.length) {
          tl.to(veilHeads, {
            x: function () { return -plateShift(); },
            y: function () { return -plateLift(); },
            ease: 'power1.in',
            duration: 0.30
          }, 0);
          tl.to(veilHeads, { autoAlpha: 0, duration: 0.12, ease: 'none' }, 0.16);
        }
      }
      if (frame) tl.to(frame, { xPercent: -90, yPercent: -28, ease: 'power1.in', duration: 0.30 }, 0);
      if (cells.length) tl.to(cells, { autoAlpha: 0, y: -12, duration: 0.24, stagger: { each: 0.0045, from: 0 }, ease: 'none' }, 0.02);
      if (chrome.length) tl.to(chrome, { autoAlpha: 0, duration: 0.16, ease: 'power1.out' }, 0.02);
      if (baseHead) tl.to(baseHead, { autoAlpha: 0, y: -40, duration: 0.16, ease: 'power1.out' }, 0.12);
      if (shadow) tl.to(shadow, { autoAlpha: 0, duration: 0.1, ease: 'none' }, 0.16);

      /* The product does NOT zoom (client, 2026-08-20). It renders at its full
         display size from the first frame and simply travels left as the veil
         retracts, landing centred on the stage.

         Previously it started small and scaled to 116% of stage height across
         almost the whole pinned scroll. Size is now owned solely by
         --ct-door-size (the per-collection `hero_size` metafield), so what the
         editor sets is what is on screen at every scroll position, and the
         render is never displayed above 100% of its own pixels. */
      if (door && doorwrap && stage) {
        var centerShift = function () {
          var dr = door.getBoundingClientRect();
          var sr = stage.getBoundingClientRect();
          return (sr.left + sr.width / 2) - (dr.left + dr.width / 2);
        };
        /* Tracks the VEIL rather than the whole scroll: the veil clears in the
           first ~30%, so the move lands with it and then holds. Read from the
           live rect on every refresh, so a resize or a catalogue swap re-measures
           instead of reusing a stale offset. */
        tl.to(doorwrap, { x: centerShift, ease: 'power2.out', duration: 0.34 }, 0.06);
      }

      /* ============ SC2: Auto scroll mode ============
         Editor choice, data-ct-scroll-mode (default 'scrub', set by
         sections/catalogue-hero.liquid from settings.hero_scroll_mode). Scrub
         is everything above: tl's own scrollTrigger scrubs it frame by frame
         and nothing below ever runs. 'auto' is a second TRIGGER for the exact
         same tl, not a second animation: it drives tl.progress() and the real
         window scroll together with a proxy tween on trapEase, the identical
         technique the data-ct-enter CTA above already uses to jump from
         wherever the visitor is to the gallery. The CTA is left untouched
         (proven, low-risk); this block reuses tl/st the same way rather than
         calling the CTA's handler directly, so the two interrupt policies
         (CTA: cancel-and-hand-back on input; auto: lock-until-landed) never
         have to share one code path. Both are guarded by the same
         self._ctaPlaying flag, so a click and an auto-trigger can never
         double-run each other.
         This entire block sits after the `if (norun) { ...; return; }` guard
         near the top of initHero, so prefers-reduced-motion (folded into
         norun) or a missing GSAP/ScrollTrigger means this code never runs and
         no listener is ever attached: reduced motion is never intercepted. */
      /* PHONES SCROLL NORMALLY (client, 2026-08-25). Auto mode is a second
         trigger that intercepts wheel, key and touch input and drives a
         scripted glide to the gallery. On a desktop that reads as a designed
         hand-off; under a thumb it reads as the page taking the scroll away,
         because a touch gesture is continuous and the lock lands mid-swipe.
         Below 900px the timeline keeps its own scrub, so the hero still
         animates as the visitor scrolls, it just no longer decides where they
         end up. Evaluated once at init, which is also when the rest of this
         hero measures itself; a phone never crosses the breakpoint. */
      var autoAllowed = true;
      try { autoAllowed = !window.matchMedia('(max-width: 900px)').matches; } catch (e) {}
      var autoMode = el.dataset.ctScrollMode === 'auto' && autoAllowed;
      if (autoMode) {
        var st = tl.scrollTrigger;
        var autoDuration = (parseFloat(el.dataset.ctAutoDuration) || 1100) / 1000;
        var autoLocked = false;

        /* A catalogue swap (swapCataloguePage) replaces this hero's whole DOM
           subtree via replaceWith(), but the window-level listeners below are
           never explicitly torn down by that swap. Without this check, a
           stale listener from a REPLACED hero stays attached, and on the next
           wheel/key/touch event it races the new hero's own listener for the
           shared self._ctaPlaying flag. If the stale one wins, it drives its
           OWN detached `el`/`stage` (both report 0 for every measurement once
           removed from the document), so toScroll computes to a meaningless
           0, the visible transition never lands, and the (still live)
           listener below never gets a turn. Every entry point below checks
           this first and self-tears-down on first stale firing, so a swap
           leaves at most one dead event per listener type before it removes
           itself for good. */
        var stale = function () { return !document.documentElement.contains(el); };
        var teardownAuto = function () {
          window.removeEventListener('wheel', onWheel);
          window.removeEventListener('keydown', onKeydown);
          window.removeEventListener('touchstart', onTouchStart);
          window.removeEventListener('touchmove', onTouchMove);
          window.removeEventListener('scroll', onScroll);
          if (settleTimer) window.clearTimeout(settleTimer);
          if (armTimer) window.clearTimeout(armTimer);
        };

        /* Document-position of the hero's own top, and of the two states tl
           scrubs between (progress 0 = rest, progress 1 = gallery top). Same
           formula the CTA uses for its landing target, just computed fresh on
           every call instead of once at click time, so it stays correct
           whichever direction is running. */
        var heroTop = function () { return el.getBoundingClientRect().top + self.scrollTop(); };
        var forwardTarget = function () { return heroTop() + el.offsetHeight - (stage ? stage.offsetHeight : window.innerHeight); };
        var restTarget = function () { return heroTop(); };

        /* The shared driver. toScrollFn is called once, at start, exactly like
           the CTA computes its `target` once per click. A hard setTimeout
           backstop always fires `dur` (+buffer) after the tween starts and
           force-completes if onComplete is ever lost (a thrown error inside
           GSAP's update, a killed tween, anything): the lock can never outlive
           the backstop, so the visitor is never trapped. */
        var runProxyTransition = function (toProgress, toScrollFn, dur, opts) {
          if (self._ctaPlaying) return false;
          self._ctaPlaying = true;

          var behaviorTargets = [self.scroller(), document.scrollingElement, document.documentElement, document.body].filter(Boolean);
          var prevBehavior = behaviorTargets.map(function (n) { return n.style.scrollBehavior; });
          behaviorTargets.forEach(function (n) { n.style.scrollBehavior = 'auto'; });

          var toScroll = toScrollFn();
          var proxy = { p: st.progress, s: self.scrollTop() };
          var done = false;
          var backstop = null;

          var restoreBehavior = function () {
            behaviorTargets.forEach(function (n, i) { n.style.scrollBehavior = prevBehavior[i] || ''; });
          };
          var finish = function (finalProgress, finalScroll) {
            if (done) return;
            done = true;
            if (backstop) { window.clearTimeout(backstop); backstop = null; }
            try { gsap.killTweensOf(proxy); } catch (e) {}
            try { tl.progress(finalProgress); } catch (e) {}
            self.setScrollTop(finalScroll);
            try { st.enable(); } catch (e) {}
            try { st.scroll(self.scrollTop()); } catch (e) {}
            if (window.ScrollTrigger) { try { window.ScrollTrigger.update(); } catch (e) {} }
            restoreBehavior();
            self._ctaPlaying = false;
            if (opts && opts.onDone) opts.onDone();
          };

          backstop = window.setTimeout(function () { finish(toProgress, toScroll); }, Math.round(dur * 1000) + 500);

          try { st.disable(false); } catch (e) {}
          gsap.to(proxy, {
            p: toProgress,
            s: toScroll,
            duration: dur,
            ease: trapEase,
            onUpdate: function () {
              try { tl.progress(proxy.p); } catch (e) {}
              self.setScrollTop(proxy.s);
            },
            onComplete: function () { finish(toProgress, toScroll); }
          });
          return true;
        };

        /* Lock happens at three points: progress essentially 0 (hero at
           rest) for the downward trigger; a RAMP ZONE anchored to the
           gallery's own top edge, which catches an upward gesture and
           glides it to rest at that top edge (Stage A); and, only once
           settled there AND a separate later gesture is detected, the
           actual reverse (Stage B). Any other scroll position (deep in the
           gallery, mid-hero after a resize glitch) fails every check and
           every listener below is a no-op, so input passes through to the
           browser untouched.

           This gate has now been wrong three times:
           - V1 used `st.progress >= 0.999`. tl's scrollTrigger ends at the
             gallery's top ('bottom bottom' on the hero), so past that point
             ScrollTrigger clamps st.progress at 1 for the ENTIRE rest of the
             document, not just at the boundary. Any upward wheel/touch
             anywhere below the hero, including deep in the gallery or the
             footer, satisfied the check and re-armed the reverse: the whole
             page felt broken.
           - SC3 (v2) overcorrected to one exact point, `Math.abs(scrollTop -
             galleryTop) <= 4`. Four pixels cannot catch a real gesture (one
             wheel notch moves 100px or more), so the visitor's position
             almost always lands on one side of that 4px line and the
             reverse never armed.
           - SC4 (v3) replaced the point with a BAND that FIRES the reverse
             directly: arm whenever scroll position sits between the
             gallery's top and BUFFER px below it. That caught the gesture
             (fixing v2), but it fired the actual hero-return transition on
             the very same continuous scroll that carried the visitor into
             the band, so they could never actually reach and rest at the
             gallery top: the client's exact complaint for this round.

           v4 (this version) keeps v3's band, anchored the same way and with
           the same viewport-relative floor so it still reliably catches a
           real gesture, but changes what the band DOES: entering it no
           longer fires the return, it starts a gentle glide that settles
           the visitor exactly at the gallery top and stops (Stage A). The
           return (Stage B) only runs once BOTH: the visitor is at that rest
           position (atGalleryRest, a tight tolerance, not the wide band),
           AND returnArmed is true, which only happens after
           RAMP_INPUT_PAUSE ms of no wheel/touchmove input. Every wheel/
           touchmove event that lands while inside the ramp zone or at rest
           restarts that pause timer (pokeArmTimer), so the very ticks that
           carry a continuous gesture through the ramp and into rest keep
           deferring arming right up to their last tick: arming can only
           complete AFTER that gesture has fully stopped. A gesture already
           in flight when the settle completes therefore cannot also fire
           Stage B; only a later, genuinely separate gesture can. This is
           the gesture boundary the client asked for, and it is what stops
           v3's failure mode without reintroducing v1's (still anchored to
           the gallery's measured offset, so deep-gallery scrolling is
           unaffected) or v2's (the zone is still wide enough to be entered
           by an ordinary gesture). */
        var galleryEl = document.getElementById('ct-collection');
        var GALLERY_TOP_TOL = 4; /* px; how close counts as "at rest" at the gallery top */
        var RAMP_INPUT_PAUSE = 150; /* ms; wheel/touchmove quiet gap required after a
          settle before a further upward gesture is treated as a NEW one. Not exposed
          as a setting: it is the gesture-boundary mechanism itself, not a tunable
          design choice like the zone depth or the glide duration below. */
        var rampZoneSetting = parseFloat(el.dataset.ctAutoBuffer) || 300;
        var RAMP_ZONE = Math.max(rampZoneSetting, window.innerHeight * 0.35);
        var rampDuration = (parseFloat(el.dataset.ctRampDuration) || 260) / 1000;
        var atRest = function () { return st.progress <= 0.001; };
        var galleryTopPos = function () {
          return galleryEl ? galleryEl.getBoundingClientRect().top + self.scrollTop() : null;
        };
        var atGalleryRest = function () {
          if (!galleryEl || !document.documentElement.contains(galleryEl)) return false;
          var top = galleryTopPos();
          var pos = self.scrollTop();
          return pos >= top - GALLERY_TOP_TOL && pos <= top + GALLERY_TOP_TOL;
        };
        var inRampZone = function () {
          if (!galleryEl || !document.documentElement.contains(galleryEl)) return false;
          var top = galleryTopPos();
          var pos = self.scrollTop();
          return pos > top + GALLERY_TOP_TOL && pos <= top + RAMP_ZONE;
        };
        var interactiveTarget = function () {
          var a = document.activeElement;
          var tag = (a && a.tagName) || '';
          return /INPUT|TEXTAREA|SELECT/.test(tag) || (a && a.isContentEditable);
        };

        /* Stage B gesture boundary. returnArmed only ever flips true inside
           the timeout below, never directly from an input handler, so it
           always reflects "input has been quiet for RAMP_INPUT_PAUSE ms",
           never "input just happened". */
        var returnArmed = false;
        var armTimer = null;
        var pokeArmTimer = function () {
          if (armTimer) window.clearTimeout(armTimer);
          armTimer = window.setTimeout(function () { returnArmed = true; armTimer = null; }, RAMP_INPUT_PAUSE);
        };
        var disarmReturn = function () {
          returnArmed = false;
          if (armTimer) { window.clearTimeout(armTimer); armTimer = null; }
        };
        var rampLocked = false; /* declared here, ahead of every function below
          that reads it, so nothing relies on var hoisting for correctness. */

        var triggerDown = function (ev) {
          if (stale() || autoLocked || rampLocked || self._ctaPlaying || cycling || self._swapping || !atRest()) return false;
          if (ev) ev.preventDefault();
          autoLocked = true;
          runProxyTransition(1, forwardTarget, autoDuration, { onDone: function () { autoLocked = false; } });
          return true;
        };
        /* Stage A: catches an upward gesture inside the ramp zone and glides
           the visitor to rest at the gallery top. tl.progress does not move
           (holdProgress is the value it already has, essentially 1), so the
           hero transition itself never runs here, only the scroll position
           eases to a stop. Reuses runProxyTransition wholesale, so this
           stage gets the exact same lock (via self._ctaPlaying) and wall-
           clock backstop as the real transition, it can never strand the
           page mid-glide. */
        var triggerRamp = function (ev) {
          if (stale() || autoLocked || rampLocked || self._ctaPlaying || cycling || self._swapping || !inRampZone()) return false;
          if (ev) ev.preventDefault();
          rampLocked = true;
          var holdProgress = st.progress;
          runProxyTransition(holdProgress, galleryTopPos, rampDuration, {
            onDone: function () {
              rampLocked = false;
              /* Also poke here, not just from the input handlers: this is
                 what makes "set the flag when the settle finishes" true
                 regardless of input source (covers a keyboard-only visitor,
                 where onKeydown deliberately never pokes, and is a harmless
                 restart of the same countdown for wheel/touch, which are
                 already poking every tick). */
              pokeArmTimer();
            }
          });
          return true;
        };
        /* Stage B: the real reverse. Now gated on returnArmed as well as
           position, so it can only fire on a gesture that started after an
           input pause, never on the gesture that produced the settle. */
        var triggerUp = function (ev) {
          if (stale() || autoLocked || rampLocked || self._ctaPlaying || cycling || self._swapping) return false;
          if (!returnArmed || !atGalleryRest()) return false;
          if (ev) ev.preventDefault();
          autoLocked = true;
          disarmReturn();
          runProxyTransition(0, restTarget, autoDuration, { onDone: function () { autoLocked = false; } });
          return true;
        };

        /* Wheel / trackpad. While locked (either stage), further input is
           swallowed (preventDefault, no state change) rather than
           cancelling the in-flight tween: the client's chosen policy is
           lock-until-landed, not cancel-on-input (that policy stays on the
           CTA above only). Every wheel event that lands near the rest point
           or inside the ramp zone pokes the arm timer regardless of
           direction, exactly per the "no wheel or touchmove events" wording
           of the gesture-boundary requirement; everywhere else it disarms,
           so deep-gallery scrolling never leaves a stale arm behind. */
        var onWheel = function (ev) {
          if (stale()) { teardownAuto(); return; }
          if (interactiveTarget()) return;
          if (rampLocked || autoLocked) {
            /* A wheel tick swallowed mid-lock is still input: it must keep
               deferring the arm timer the same as any other tick, or the
               timer (started once, when the ramp first fired) can expire
               WHILE the glide is still running and the gesture that drove it
               is still going, arming Stage B before that gesture has
               actually ended. That was the exact way v3's bug reappeared
               under this new mechanism during testing: the visitor's
               fingers never left the wheel, but the pause timer fired
               mid-glide anyway and the very next (still-continuous) tick
               triggered the return the instant the glide landed. */
            if (rampLocked) pokeArmTimer();
            ev.preventDefault();
            return;
          }
          if (atGalleryRest() || inRampZone()) { pokeArmTimer(); } else { disarmReturn(); }
          if (ev.deltaY > 0) { triggerDown(ev); return; }
          if (ev.deltaY < 0) {
            if (triggerUp(ev)) return;
            if (triggerRamp(ev)) return;
            /* Settled but not yet armed, or between locks: hold the visitor
               here rather than let native scroll drift them back into the
               hero's pinned range before the gesture boundary clears. */
            if (atGalleryRest()) ev.preventDefault();
          }
        };
        window.addEventListener('wheel', onWheel, { passive: false });

        /* Keyboard: only scroll-intent keys are ever intercepted (Space,
           Page Down/Up, Arrow Down/Up). Every other key, including Tab, is
           never touched by this listener, so focus order and keyboard
           navigation are unaffected. Skips form fields the same way the
           existing next/prev ArrowLeft/ArrowRight handler already does.
           Keydown does not poke the arm timer (the gesture-boundary spec
           names wheel/touchmove only); it still respects returnArmed, so a
           held Arrow Up cannot skip the pause requirement either. */
        var onKeydown = function (ev) {
          if (stale()) { teardownAuto(); return; }
          if (ev.defaultPrevented || interactiveTarget()) return;
          var isSpace = ev.key === ' ' || ev.code === 'Space';
          var isDown = ev.key === 'PageDown' || ev.key === 'ArrowDown' || (isSpace && !ev.shiftKey);
          var isUp = ev.key === 'PageUp' || ev.key === 'ArrowUp' || (isSpace && ev.shiftKey);
          if (!isDown && !isUp) return;
          if (rampLocked || autoLocked) { ev.preventDefault(); return; }
          if (isDown) { triggerDown(ev); return; }
          if (triggerUp(ev)) return;
          if (triggerRamp(ev)) return;
          if (atGalleryRest()) ev.preventDefault();
        };
        window.addEventListener('keydown', onKeydown, { passive: false });

        /* Touch: written per the brief (handle it in code) but not part of
           the 1440px desktop verification. A vertical drag past a small
           threshold counts as one gesture, same lock policy as wheel/key.
           The arm-timer poke runs on every touchmove regardless of the
           threshold, same reasoning as wheel: any touchmove event is input
           activity for the gesture-boundary timer, per spec wording. */
        var touchY = null;
        var onTouchStart = function (ev) {
          if (stale()) { teardownAuto(); return; }
          touchY = (ev.touches && ev.touches[0]) ? ev.touches[0].clientY : null;
        };
        window.addEventListener('touchstart', onTouchStart, { passive: true });
        var onTouchMove = function (ev) {
          if (stale()) { teardownAuto(); return; }
          if (touchY === null || interactiveTarget()) return;
          if (rampLocked || autoLocked) {
            /* Same reasoning as the wheel handler above: a touchmove
               swallowed mid-lock is still the same finger, still moving, so
               it must keep deferring the arm timer too. */
            if (rampLocked) pokeArmTimer();
            ev.preventDefault();
            return;
          }
          if (atGalleryRest() || inRampZone()) { pokeArmTimer(); } else { disarmReturn(); }
          var y = (ev.touches && ev.touches[0]) ? ev.touches[0].clientY : null;
          if (y === null) return;
          var dy = touchY - y;
          if (Math.abs(dy) < 12) return;
          touchY = null;
          if (dy > 0) { triggerDown(ev); return; }
          if (triggerUp(ev)) return;
          if (triggerRamp(ev)) return;
          if (atGalleryRest()) ev.preventDefault();
        };
        window.addEventListener('touchmove', onTouchMove, { passive: false });

        /* Overshoot safety net. A single very fast flick can jump the native
           scroll position from deep in the gallery to somewhere inside the
           hero's pinned range in one native scroll (no wheel/keydown/
           touchmove event ever samples a position inside the catch band
           above, because the position check runs on the PRE-scroll offset
           and that one event's own delta covers the whole band in one go).
           When that happens the BASE ScrollTrigger's own scrub (scrub: 0.18
           on tl itself, always live outside an active proxy transition)
           still reacts to the raw scroll position, so tl can be left at a
           fractional progress with no landing animation queued: neither
           hero rest nor gallery top, a visibly half-done state. Once
           scrolling goes quiet (debounced; the proxy tween's own onUpdate
           also writes scrollTop and so also fires 'scroll', which the
           autoLocked/_ctaPlaying guard below ignores) with progress stranded
           strictly between the two rest states, snap to whichever end is
           nearer using the same proxy-tween helper the discrete triggers
           use, so the hero always finishes at one of its two real states. */
        var settleTimer = null;
        var onScrollSettle = function () {
          if (stale()) { teardownAuto(); return; }
          if (autoLocked || self._ctaPlaying || cycling || self._swapping) return;
          var p = st.progress;
          if (p <= 0.001 || p >= 0.999) return;
          autoLocked = true;
          var toRest = p < 0.5;
          runProxyTransition(toRest ? 0 : 1, toRest ? restTarget : forwardTarget, autoDuration, { onDone: function () { autoLocked = false; } });
        };
        var onScroll = function () {
          if (stale()) { teardownAuto(); return; }
          if (settleTimer) window.clearTimeout(settleTimer);
          settleTimer = window.setTimeout(onScrollSettle, 160);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      }

    },

    /* ================= GALLERY ================= */
    initGallery: function (el) {
      if (!el || el.dataset.ctBound === '1') return;
      el.dataset.ctBound = '1';

      var self = this;
      var reveals = el.querySelectorAll('.ct-reveal');
      var rows = el.querySelectorAll('[data-ct-scene]');
      var rail = el.querySelector('[data-ct-rail]');
      var railFill = el.querySelector('[data-ct-rail-fill]');
      var mode = el.dataset.ctMode || 'hover';
      var scrub = !FINE || mode === 'scroll';

      if (REDUCED) {
        for (var r = 0; r < reveals.length; r++) reveals[r].classList.add('is-in');
        return;
      }

      var io = this.revealObserver();
      for (var k = 0; k < reveals.length; k++) io.observe(reveals[k]);

      /* DUO editorial spreads: an orchestrated entrance (duo + detail cascade)
         plus a scrubbed name-line rise driven by the spread's own scroll
         travel, plus a gentle scroll parallax on the images for depth.
         Entrance plays once as each spread enters; the name rise and the
         parallax are both tied to scroll position. Parallax is desktop only
         so mobile's stacked, statically-placed layout never shifts. */
      var spreads = el.querySelectorAll('[data-ct-spread]');
      if (spreads.length && window.gsap && hasST()) {
        var gsapD = window.gsap;
        /* Entrance: plays once per spread as it enters, at every width. */
        for (var sp = 0; sp < spreads.length; sp++) {
          (function (spread) {
            var lines = spread.querySelectorAll('.ctd-name__t');
            var slots = spread.querySelectorAll('.ctd-slot');
            var altMedia = spread.querySelectorAll('.ctd-single, .ctd-cards, .ctd-frame');
            var dets = spread.querySelectorAll('.ctd-det');
            var meta = spread.querySelector('.ctd-meta');

            var entrance = gsapD.timeline({
              scrollTrigger: { trigger: spread, start: 'top 82%', once: true }
            });
            if (slots.length) entrance.fromTo(slots, { autoAlpha: 0, y: 48 }, { autoAlpha: 1, y: 0, duration: 1.15, ease: 'power3.out', stagger: 0.12 }, 0.18);
            /* SINGLE / CARDS media block: reveal the wrapper (its inner cross-fade
               or float keeps running underneath, so we never touch its transform). */
            if (altMedia.length) entrance.fromTo(altMedia, { autoAlpha: 0, y: 48 }, { autoAlpha: 1, y: 0, duration: 1.15, ease: 'power3.out' }, 0.18);
            if (dets.length) entrance.fromTo(dets, { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 0.9, ease: 'power3.out', stagger: 0.09 }, 0.42);
            if (meta) entrance.fromTo(meta, { autoAlpha: 0, y: 22 }, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out' }, 0.56);

            /* Title: a scrubbed rise rather than a play-once entrance. The two
               masked lines rise in direct proportion to the spread's travel
               through the viewport, so the name assembles under the visitor's
               control and reverses on scroll-up. The mask is CSS
               (.ctd-name__l { overflow: hidden }); only the driver changes. */
            if (lines.length) {
              gsapD.fromTo(lines,
                { yPercent: 118 },
                {
                  yPercent: 0,
                  ease: 'none',
                  stagger: 0.08,
                  scrollTrigger: {
                    trigger: spread,
                    start: 'top 90%',
                    end: 'top 45%',
                    scrub: 0.5
                  }
                }
              );
            }
          })(spreads[sp]);
        }
        /* Parallax: desktop only AND responsive. gsap.matchMedia reverts these
           scrub tweens (clearing the inline transforms) when the viewport drops
           below the breakpoint, so the mobile stacked layout never drifts, and
           rebuilds them if it grows back. Mirrors the CSS 900/901 boundary. */
        gsapD.matchMedia().add('(min-width: 901px)', function () {
          for (var p = 0; p < spreads.length; p++) {
            (function (spread) {
              var duo = spread.querySelector('.ctd-duo');
              var strip = spread.querySelector('.ctd-strip');
              var para = gsapD.timeline({
                scrollTrigger: { trigger: spread, start: 'top bottom', end: 'bottom top', scrub: 0.4 }
              });
              if (duo) para.fromTo(duo, { yPercent: -4 }, { yPercent: 4, ease: 'none' }, 0);
              if (strip) para.fromTo(strip, { yPercent: 7 }, { yPercent: -7, ease: 'none' }, 0);
            })(spreads[p]);
          }
        });
      }

      /* Hover mode: intent delay, then CSS runs the zoom + angle swap. */
      if (!scrub && FINE) {
        for (var h = 0; h < rows.length; h++) {
          (function (row) {
            var timer = null;
            row.addEventListener('mouseenter', function () {
              timer = window.setTimeout(function () { row.classList.add('is-hover'); }, 130);
            });
            row.addEventListener('mouseleave', function () {
              if (timer) window.clearTimeout(timer);
              row.classList.remove('is-hover');
            });
          })(rows[h]);
        }
      }

      /* Scroll mode (and every coarse pointer): the swap is scrubbed by the
         scene's travel through the viewport; small parallax on the frame. */
      var vis = false;
      var gate = new IntersectionObserver(function (entries) {
        vis = entries[0] ? entries[0].isIntersecting : false;
        if (rail) rail.classList.toggle('is-on', vis);
        if (vis) self.addTick(tick); else self.removeTick(tick);
      }, { root: this.scroller(), rootMargin: '10% 0px 10% 0px', threshold: 0 });
      gate.observe(el);

      if (scrub) {
        for (var m = 0; m < rows.length; m++) rows[m].classList.add('ct-scrub');
      }

      var vh = function () { return window.innerHeight; };
      var tick = function () {
        if (scrub) {
          for (var i = 0; i < rows.length; i++) {
            var row = rows[i];
            var rect = row.getBoundingClientRect();
            if (rect.bottom < -80 || rect.top > vh() + 80) continue;
            var t = (vh() * 0.62 - rect.top) / (vh() * 0.58);
            t = Math.max(0, Math.min(1, t));
            var a = row.querySelector('.ct-img-a');
            var b = row.querySelector('.ct-img-b');
            var frame = row.querySelector('[data-ct-frame]');
            var swap = t < 0.35 ? 0 : t > 0.72 ? 1 : (t - 0.35) / 0.37;
            if (a) a.style.opacity = String(1 - swap);
            if (b) b.style.opacity = String(swap);
            if (frame) frame.style.transform = 'translateY(' + ((0.5 - t) * 3.2) + 'vh)';
          }
        }
        if (railFill) {
          var g = el.getBoundingClientRect();
          var span = g.height - vh();
          var p = span > 0 ? Math.max(0, Math.min(1, -g.top / span)) : 0;
          railFill.style.transform = 'scaleY(' + p + ')';
        }
      };

      /* Pointer depth: a whisper of tilt toward the cursor (hover mode). */
      if (!scrub && FINE && window.gsap) {
        for (var td = 0; td < rows.length; td++) {
          (function (row) {
            var media = row.querySelector('.ct-media');
            if (!media) return;
            var rx = window.gsap.quickTo(media, 'rotationX', { duration: 0.7, ease: 'power2.out' });
            var ry = window.gsap.quickTo(media, 'rotationY', { duration: 0.7, ease: 'power2.out' });
            row.addEventListener('pointermove', function (ev) {
              var r = row.getBoundingClientRect();
              var nx = ((ev.clientX - r.left) / r.width) * 2 - 1;
              var ny = ((ev.clientY - r.top) / r.height) * 2 - 1;
              ry(nx * 2);
              rx(ny * -2);
            }, { passive: true });
            row.addEventListener('mouseleave', function () { ry(0); rx(0); });
          })(rows[td]);
        }
      }

      /* Cursor pill (editor toggle; ships off). */
      if (el.hasAttribute('data-ct-pill') && FINE && window.gsap) {
        var pill = document.createElement('div');
        pill.className = 'ct-pill';
        pill.textContent = 'View';
        document.body.appendChild(pill);
        var qx = window.gsap.quickTo(pill, 'x', { duration: 0.3, ease: 'power2.out' });
        var qy = window.gsap.quickTo(pill, 'y', { duration: 0.3, ease: 'power2.out' });
        el.addEventListener('pointermove', function (ev) { qx(ev.clientX); qy(ev.clientY); }, { passive: true });
        for (var p2 = 0; p2 < rows.length; p2++) {
          (function (row) {
            row.addEventListener('mouseenter', function () { pill.classList.add('is-on'); });
            row.addEventListener('mouseleave', function () { pill.classList.remove('is-on'); });
          })(rows[p2]);
        }
      }

      /* Safety net: never leave content hidden if IO misfires. */
      window.addEventListener('load', function () {
        setTimeout(function () {
          for (var n = 0; n < reveals.length; n++) {
            if (!reveals[n].classList.contains('is-in')) {
              var rect = reveals[n].getBoundingClientRect();
              if (rect.top < window.innerHeight * 1.1) reveals[n].classList.add('is-in');
            }
          }
        }, 400);
      });
    }
  };

  window.IronstoneCatalogue = IronstoneCatalogue;

  function boot() { IronstoneCatalogue.init(document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The hero image loads after init and shifts measurements; recompute. */
  window.addEventListener('load', function () {
    if (window.ScrollTrigger) { try { window.ScrollTrigger.refresh(); } catch (e) {} }
  });

  document.addEventListener('shopify:section:load', function (e) {
    IronstoneCatalogue._scroller = undefined;
    if (window.ScrollTrigger) {
      try {
        window.ScrollTrigger.getAll().forEach(function (t) {
          if (t.trigger && !document.contains(t.trigger)) t.kill();
        });
      } catch (err) {}
    }
    IronstoneCatalogue.init(e.target);
  });
})();
